import { useMutation } from '@apollo/client';
import { RetriggerOrganisationWebhookDeliveryDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import type { RetriggerDeliveryResult } from './useRetriggerDeliveryTRPC';
import { useRetriggerDeliveryTRPC } from './useRetriggerDeliveryTRPC';

const RETRIGGER_STATUSES = [
  'queued',
  'already_queued',
  'not_replayable',
] as const;

const isRetriggerStatus = (
  value: string
): value is RetriggerDeliveryResult['status'] =>
  RETRIGGER_STATUSES.some((status) => status === value);

// No cache invalidation (no Apollo `update`/evictField): the re-queued row only
// appears ~1 min later, so refetching now shows nothing new.
export const useRetriggerDelivery = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [retriggerGraphQL, graphqlState] = useMutation(
    RetriggerOrganisationWebhookDeliveryDocument
  );

  const trpcMutation = useRetriggerDeliveryTRPC();

  const retriggerDelivery = async (
    id: string
  ): Promise<RetriggerDeliveryResult> => {
    if (trpcEnabled) {
      return trpcMutation.retriggerDelivery(id);
    }

    const result = await retriggerGraphQL({
      variables: { object: { Id: id } },
    });
    const status = result.data?.retriggerOrganisationWebhookDeliveryApi?.status;
    if (status === undefined || !isRetriggerStatus(status)) {
      throw new Error('Failed to re-trigger delivery');
    }

    return { status };
  };

  if (trpcEnabled) {
    return {
      retriggerDelivery,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    retriggerDelivery,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
